import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Thumbs } from "@/lib/ratings";
import { Heart, MoreHorizontal, Play, Pause, Plus, ListPlus, ListEnd, Trash2, ListMinus, AudioLines, Radio, Pencil, CopyPlus, ArrowUp, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { copyNow, useCanCopy } from "@/components/CopyToLibrary";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Cover } from "@/components/Cover";
import { EditTrackDialog } from "@/components/EditTrackDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectionBar, REMOVE_NOTE } from "@/components/SelectionBar";
import { usePlayer } from "@/context/PlayerContext";
import { usePlaylists } from "@/context/PlaylistsContext";
import { api, fmtTime, formatApiError } from "@/lib/api";
import { useBuildMix } from "@/lib/mix";

export const AddToPlaylistItems = ({ trackIds }) => {
  const { playlists, addTracks, create } = usePlaylists();
  const createNew = async () => {
    const name = window.prompt("New playlist name");
    if (!name) return;
    await create(name, trackIds);
    toast.success(`Created ${name}`);
  };
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid="add-to-playlist-trigger"><ListPlus size={14} className="mr-2" />Add to playlist</DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
        <DropdownMenuItem onClick={createNew} data-testid="add-to-new-playlist"><Plus size={14} className="mr-2" />New playlist…</DropdownMenuItem>
        {playlists.length > 0 && <DropdownMenuSeparator />}
        {playlists.filter((p) => p.source !== "rules").map((p) => (
          <DropdownMenuItem key={p.id} onClick={() => addTracks(p.id, trackIds)} data-testid={`add-to-playlist-${p.id}`}>{p.name}</DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

const RowMenu = ({ track, onRemove, onDeleted, onEdit, shared = false }) => {
  const p = usePlayer();
  const canCopy = useCanCopy() && (shared || track.lib === "owner");
  const del = async () => {
    if (!window.confirm(`Delete "${track.title}" from your library?`)) return;
    try {
      await api.delete(`/tracks/${track.id}`);
      toast.success("Track deleted");
      onDeleted?.(track.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={`track-menu-${track.id}`} className="rounded p-1 text-zinc-400 opacity-0 transition-opacity hover:text-zinc-900 focus:opacity-100 group-hover:opacity-100">
        <MoreHorizontal size={16} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={() => p.playNext([track])} data-testid="track-play-next"><ListEnd size={14} className="mr-2" />Play next</DropdownMenuItem>
        <DropdownMenuItem onClick={() => { p.addToQueue([track]); toast.success("Added to queue"); }} data-testid="track-add-queue"><Plus size={14} className="mr-2" />Add to queue</DropdownMenuItem>
        <DropdownMenuItem onClick={() => p.startRadio("track", track.id)} data-testid="track-start-radio"><Radio size={14} className="mr-2" />Start song radio</DropdownMenuItem>
        <AddToPlaylistItems trackIds={[track.id]} />
        {onRemove && <DropdownMenuItem onClick={() => onRemove(track)} data-testid="track-remove-from-playlist"><ListMinus size={14} className="mr-2" />Remove from playlist</DropdownMenuItem>}
        {canCopy && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => copyNow({ track_ids: [track.id] })} data-testid="track-copy-to-library"><CopyPlus size={14} className="mr-2" />Copy to my library</DropdownMenuItem>
          </>
        )}
        {track.source === "upload" && !shared && track.lib !== "owner" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEdit?.(track)} data-testid="track-edit"><Pencil size={14} className="mr-2" />Edit details</DropdownMenuItem>
            <DropdownMenuItem onClick={del} className="text-red-600" data-testid="track-delete"><Trash2 size={14} className="mr-2" />Delete from Resonance storage</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SortHeader = ({ label, field, sortable, className = "" }) => {
  if (!sortable) return <span className={className}>{label}</span>;
  const active = sortable.sort === field;
  return (
    <button type="button" onClick={() => sortable.onSort(field)} data-testid={`track-sort-${field}`} aria-sort={active ? "ascending" : "none"}
      className={`flex items-center gap-1 text-left uppercase tracking-[0.18em] transition-colors hover:text-zinc-900 ${active ? "text-zinc-900" : ""} ${className}`}>
      {label}{active && <ArrowUp size={10} />}
    </button>
  );
};

export const TrackTable = ({ tracks, showAlbum = true, showCover = false, numbered = true, onRemove, onTracksChange, selectable = false, removable = true, readOnly = false, sortable = null, onReorder = null, selectionRef = null, onSelectionChange = null }) => {
  const dragFrom = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const drop = (to) => {
    const from = dragFrom.current;
    dragFrom.current = null; setDragOver(null);
    if (from === null || from === to) return;
    const next = [...rows]; const [m] = next.splice(from, 1); next.splice(to, 0, m);
    setRows(next); onReorder(from, to, next);
  };
  const p = usePlayer();
  const buildMix = useBuildMix();
  const [rows, setRows] = useState(tracks);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    setRows(tracks);
    setSel((s) => { const ids = new Set(tracks.map((t) => t.id)); const n = new Set([...s].filter((id) => ids.has(id))); return n.size === s.size ? s : n; });
  }, [tracks]);
  const update = (next) => { setRows(next); onTracksChange?.(next); };
  const onEdited = (t) => update(t.album_changed && showAlbum === false ? rows.filter((r) => r.id !== t.id) : rows.map((r) => (r.id === t.id ? { ...r, ...t } : r)));
  const anchor = useRef(null);
  const lastShift = useRef(false);
  const toggleSel = (id) => {
    const n = new Set(sel);
    const ids = rows.map((r) => r.id);
    if (lastShift.current && anchor.current && anchor.current !== id && ids.includes(anchor.current)) {
      const [a, b] = [ids.indexOf(anchor.current), ids.indexOf(id)].sort((x, y) => x - y);
      ids.slice(a, b + 1).forEach((x) => n.add(x));
    } else {
      n.has(id) ? n.delete(id) : n.add(id);
    }
    anchor.current = id;
    setSel(n);
  };
  useEffect(() => {
    if (selectionRef) selectionRef.current = {
      all: () => setSel(new Set(rows.map((r) => r.id))), none: () => setSel(new Set()),
      invert: () => setSel(new Set(rows.filter((r) => !sel.has(r.id)).map((r) => r.id))),
      where: (pred) => setSel(new Set(rows.filter(pred).map((r) => r.id))), rows, count: sel.size,
    };
    onSelectionChange?.(sel.size, rows.length);
  }, [sel, rows]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selectable) return undefined;
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || "") || document.activeElement?.isContentEditable;
      if (e.key === "Escape" && sel.size && !document.querySelector('[role="menu"], [role="dialog"]')) setSel(new Set());
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a" && !typing) { e.preventDefault(); setSel(new Set(rows.map((r) => r.id))); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectable, sel.size, rows]);
  const favSelected = async () => {
    const picked = rows.filter((r) => sel.has(r.id));
    const want = picked.some((r) => !r.starred);
    setBusy(true);
    try {
      const todo = picked.filter((r) => !!r.starred !== want).map((r) => r.id);
      const { data } = await api.post("/tracks/star-bulk", { track_ids: todo, starred: want });
      const done = new Set(data.track_ids);
      update(rows.map((r) => (done.has(r.id) ? { ...r, starred: want } : r)));
      done.forEach((id) => p.updateTrack(id, { starred: want }));
      toast.success(`${want ? "Added" : "Removed"} ${done.size} song${done.size === 1 ? "" : "s"} ${want ? "to" : "from"} favorites`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const removeSelected = async () => {
    if (!window.confirm(`Remove ${sel.size} song${sel.size === 1 ? "" : "s"} from your library?\n\n${REMOVE_NOTE}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/library/remove", { track_ids: [...sel] });
      update(rows.filter((r) => !sel.has(r.id)));
      setSel(new Set());
      toast.success(`Removed ${data.tracks} song${data.tracks === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const cols = selectable ? "grid-cols-[1.25rem_2rem_1fr_auto] md:grid-cols-[1.25rem_2rem_1fr_1fr_4rem_5rem]" : "grid-cols-[2rem_1fr_auto] md:grid-cols-[2rem_1fr_1fr_4rem_5rem]";

  const star = async (t) => {
    const { data } = await api.post(`/tracks/${t.id}/star`);
    update(rows.map((r) => (r.id === t.id ? { ...r, starred: data.starred } : r)));
    p.updateTrack(t.id, { starred: data.starred });
  };

  if (!rows.length) return <p className="py-10 text-sm text-zinc-500" data-testid="track-table-empty">No tracks here yet.</p>;

  return (
    <div className="w-full" data-testid="track-table">
      <div className={`grid ${cols} gap-4 border-b border-zinc-200 px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400`}>
        {selectable && (
          <Checkbox checked={rows.length > 0 && sel.size === rows.length} onCheckedChange={(v) => setSel(v ? new Set(rows.map((r) => r.id)) : new Set())}
            data-testid="track-select-all-checkbox" aria-label="Select all" />
        )}
        <span>#</span><SortHeader label="Title" field="title" sortable={sortable} />
        {showAlbum ? <SortHeader label="Album" field="album" sortable={sortable} className="hidden md:flex" /> : <SortHeader label="Year" field="year" sortable={sortable} className="hidden md:flex" />}
        <span className="hidden text-right md:block">Time</span><span />
      </div>
      {rows.map((t, i) => {
        const isCurrent = p.current?.id === t.id;
        const picked = sel.has(t.id);
        return (
          <div key={`${t.id}-${i}`} data-testid={`track-row-${t.id}`} onDoubleClick={() => p.playTracks(rows, i)}
            draggable={!!onReorder} onDragStart={onReorder ? (e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = "move"; } : undefined}
            onDragOver={onReorder ? (e) => { e.preventDefault(); if (dragOver !== i) setDragOver(i); } : undefined}
            onDrop={onReorder ? (e) => { e.preventDefault(); drop(i); } : undefined} onDragEnd={onReorder ? () => { dragFrom.current = null; setDragOver(null); } : undefined}
            className={`group grid ${cols} items-center gap-4 rounded-md px-3 py-2 transition-colors duration-100 ${picked ? "bg-zinc-100" : isCurrent ? "bg-amber-50/80" : "hover:bg-zinc-100/70"} ${dragOver === i ? "ring-2 ring-inset ring-amber-400" : ""} ${onReorder ? "cursor-grab active:cursor-grabbing" : ""}`}>
            {selectable && (
              <Checkbox checked={picked} onCheckedChange={() => toggleSel(t.id)} onClick={(e) => { lastShift.current = e.shiftKey; }} data-testid={`track-select-${t.id}`} aria-label={`Select ${t.title}`}
                className={`transition-opacity ${sel.size ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"}`} />
            )}
            <button onClick={() => (isCurrent ? p.toggle() : p.playTracks(rows, i))} data-testid={`track-play-${t.id}`} className="relative flex h-6 w-6 items-center justify-center font-mono text-xs text-zinc-400">
              <span className={`group-hover:hidden ${isCurrent ? "hidden" : ""}`}>{numbered ? (t.track_no && showAlbum === false ? t.track_no : i + 1) : ""}</span>
              {isCurrent && p.playing ? <AudioLines size={14} className="text-amber-600 group-hover:hidden" /> : null}
              <span className={`hidden text-zinc-900 group-hover:block ${isCurrent && !p.playing ? "!block" : ""}`}>
                {isCurrent && p.playing ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
              </span>
            </button>
            <div className="flex min-w-0 items-center gap-3">
              {showCover && <CoverThumb t={t} />}
              <div className="min-w-0">
                <p className={`truncate text-sm ${isCurrent ? "font-semibold text-amber-700" : "text-zinc-900"}`}>{t.title}</p>
                {readOnly ? <span className="truncate text-xs text-zinc-500">{t.artist}</span> :
                  <Link to={`/artist?name=${encodeURIComponent(t.artist)}`} className="truncate text-xs text-zinc-500 hover:underline">{t.artist}</Link>}
              </div>
            </div>
            <div className="hidden min-w-0 md:block">
              {showAlbum && readOnly ? <span className="truncate text-sm text-zinc-500">{t.album}</span> : showAlbum ? (
                <Link to={`/album/${t.album_id}`} className="truncate text-sm text-zinc-500 hover:text-zinc-900 hover:underline">{t.album}</Link>
              ) : <span className="truncate text-sm text-zinc-500">{t.year || ""}</span>}
            </div>
            <span className="hidden text-right font-mono text-xs text-zinc-500 md:block">{fmtTime(t.duration)}</span>
            <div className="flex items-center justify-end gap-1">
              {onReorder && <span className="hidden text-zinc-300 group-hover:text-zinc-500 md:inline" title="Drag to reorder" data-testid={`track-drag-${t.id}`}><GripVertical size={14} /></span>}
              <Thumbs trackId={t.id} testid={`track-rate-${t.id}`} className="[&>button[data-state=off]]:opacity-0 group-hover:[&>button]:opacity-100 [&>button[data-state=on]]:opacity-100" />
              <button onClick={() => star(t)} data-testid={`track-star-${t.id}`}
                className={`rounded p-1 transition-colors ${t.starred ? "text-amber-600" : "text-zinc-300 opacity-0 hover:text-zinc-900 group-hover:opacity-100"}`}>
                <Heart size={15} fill={t.starred ? "currentColor" : "none"} />
              </button>
              <RowMenu track={t} shared={readOnly} onRemove={onRemove} onDeleted={(id) => update(rows.filter((r) => r.id !== id))} onEdit={setEditing} />
            </div>
          </div>
        );
      })}
      {selectable && (
        <SelectionBar count={sel.size} noun="song" busy={busy} onRemove={removable ? removeSelected : null} onClear={() => setSel(new Set())}
          onBuildMix={() => buildMix(rows.filter((r) => sel.has(r.id)))}
          onSelectAll={sel.size < rows.length ? () => setSel(new Set(rows.map((r) => r.id))) : null}>
          {!readOnly && (
            <button onClick={favSelected} disabled={busy} data-testid="selection-favorite" className="flex h-9 items-center gap-1.5 rounded-full px-3 text-xs text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">
              <Heart size={14} />{rows.some((r) => sel.has(r.id) && !r.starred) ? "Favorite" : "Unfavorite"}
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="selection-more" className="flex h-9 items-center gap-1.5 rounded-full px-3 text-xs text-zinc-600 hover:bg-zinc-100"><ListPlus size={14} />Add to…</DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-52">
              <DropdownMenuItem onClick={() => { p.addToQueue(rows.filter((r) => sel.has(r.id))); toast.success("Added to queue"); }} data-testid="selection-add-queue"><Plus size={14} className="mr-2" />Queue</DropdownMenuItem>
              <AddToPlaylistItems trackIds={rows.filter((r) => sel.has(r.id)).map((r) => r.id)} />
            </DropdownMenuContent>
          </DropdownMenu>
        </SelectionBar>
      )}
      <EditTrackDialog track={editing} onClose={() => setEditing(null)} onSaved={onEdited} />
    </div>
  );
};

const CoverThumb = ({ t }) => <Cover albumId={t.album_id} className="h-9 w-9 shrink-0" iconSize={14} />;
